import type { AxiosError } from 'axios';
import { makeAutoObservable, runInAction } from 'mobx';
import { makePersistable } from 'mobx-persist-store';

import {
  CustomerRequests,
  type MindboxLoyaltyCard,
  type MindboxLoyaltyPointsBalance,
} from '~/api/Customer';
import type { ApiErrorResponse } from '~/api/Requests';
import { company } from '~/company/Company';
import { mainStore } from '~/stores';

import type { BaseStore } from './interfaces/BaseStore';
import { storage as localStorage } from './LocalStorage';
import { MetaStore } from './shared/MetaStore';
import type { UserStore } from './UserStore';

export class MindboxLoyaltyStore implements BaseStore {
  private readonly _userStore: UserStore;

  readonly meta: MetaStore = new MetaStore();
  readonly cardMeta: MetaStore = new MetaStore();

  enabled: boolean = false;
  balances: MindboxLoyaltyPointsBalance[] = [];
  card: MindboxLoyaltyCard | null = null;

  constructor(userStore: UserStore) {
    this._userStore = userStore;

    makeAutoObservable(this);
    makePersistable(this, {
      name: 'MindboxLoyaltyStore',
      properties: ['card'],
      storage: localStorage,
    }).catch((error) => error && console.error(error));
  }

  setEnabled(enabled: boolean) {
    this.enabled = enabled;
  }

  setBalances(balances: MindboxLoyaltyPointsBalance[]) {
    this.balances = balances;
  }

  resetCard() {
    this.card = null;
  }

  reset() {
    this.enabled = false;
    this.balances = [];
  }

  async requestBalance(): Promise<void> {
    if (!company.mindboxLoyaltyEnabled) {
      return;
    }

    if (!this._userStore.isAuthorized) {
      this.reset();
      return;
    }

    try {
      this.meta.setLoading(true);
      const response = await CustomerRequests.getCustomerLoyaltyPoints();

      runInAction(() => {
        this.enabled = response.enabled;
        this.balances = response.balances ?? [];
      });
    } catch (error: unknown) {
      runInAction(() => {
        this.reset();
      });
      await mainStore.errorHandler(error as AxiosError<ApiErrorResponse>, '');
    } finally {
      this.meta.setLoading(false);
    }
  }

  async requestLoyaltyCard(): Promise<void> {
    if (!company.mindboxLoyaltyEnabled) {
      return;
    }

    if (!this._userStore.isAuthorized) {
      return;
    }

    if (this.cardMeta.isLoading) {
      return;
    }

    try {
      this.cardMeta.setLoading(true);
      const response = await CustomerRequests.getCustomerLoyaltyCard();

      runInAction(() => {
        this.card = response.card ?? null;
      });
    } catch (error: unknown) {
      // Keep the persisted card so it stays visible offline; the modal falls back to the error state when no card is cached.
      console.error(error);
    } finally {
      this.cardMeta.setLoading(false);
    }
  }

  destroy() {
    this.meta.destroy();
    this.cardMeta.destroy();
  }
}
